"use client";

import Link from "next/link";
import { useState } from "react";

const filters = [
  { id: "all", label: "All Looks" },
  { id: "south_asian", label: "South Asian Bridal" },
  { id: "editorial", label: "Editorial Morning Prep" },
] as const;

type filter_id = (typeof filters)[number]["id"];

const active_button_class =
  "px-4 py-2 bg-on-surface text-surface rounded-full font-label-md text-label-md font-medium shadow-sm";
const idle_button_class =
  "px-4 py-2 bg-surface-container-lowest text-on-surface hover:bg-surface-container-highest transition-colors rounded-full font-label-md text-label-md font-medium";

export function PortfolioGallery() {
  const [active_filter, set_active_filter] = useState<filter_id>("all");

  return (
<div className="max-w-7xl mx-auto flex flex-col">
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
    <div className="flex flex-col max-w-xl">
      <span className="font-label-caps text-label-caps text-primary uppercase tracking-widest font-semibold">
        Real Weddings • Austin &amp; Beyond
      </span>
      <h2 className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-on-surface mt-2">
         The Visual Portfolio 
      </h2>
      <p className="font-body-md text-body-md text-on-surface-variant mt-2">
         Glimpse our brides captured in authentic moments: from early morning prep light to hill country golden hour ceremonies. 
      </p>
    </div>
    <div className="flex items-center gap-2 flex-wrap">
            {filters.map((filter) => (
              <button
                className={active_filter === filter.id ? active_button_class : idle_button_class}
                key={filter.id}
                onClick={() => set_active_filter(filter.id)}
                type="button"
              >
                {filter.label}
              </button>
            ))}
          </div>
  </div>
  <div className="grid grid-cols-1 md:grid-cols-12 gap-gutter-lg items-stretch" data-filter={active_filter}>
    <div data-look="south_asian" className="md:col-span-7 flex flex-col bg-surface-container-lowest rounded-xl overflow-hidden shadow-sm group">
      <div className="relative overflow-hidden w-full h-[520px]">
        <img alt="South Asian bride sari draping &amp; bridal hair" className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500 ease-out" src="/images/look-04.jpg" />
        <div className="absolute inset-0 bg-gradient-to-t from-on-background/80 via-transparent to-transparent opacity-80 group-hover:opacity-75 transition-opacity">
        </div>
        <div className="absolute bottom-0 left-0 p-8 flex flex-col text-surface">
          <span className="px-3 py-1 bg-surface-tint text-on-primary rounded font-label-caps text-label-caps uppercase tracking-wider self-start mb-2 font-semibold">
             Heritage Saree Draping &amp; Hair 
          </span>
          <h3 className="font-headline-md text-headline-md tracking-tight text-surface-bright font-medium">
             Regal Crimson Kanjeevaram &amp; Sculpted Floral Braid 
          </h3>
          <p className="font-body-sm text-body-sm text-surface-variant mt-2 max-w-lg">
             Captured at a hill country vista venue outside Austin, featuring authentic gold-threaded pleating and jewelry anchor technique. 
          </p>
        </div>
      </div>
      <div className="p-6 flex items-center justify-between bg-surface-container-lowest">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-primary text-[20px]">
            location_on
          </span>
          <span className="font-body-sm text-body-sm text-on-surface font-medium">
            Austin Hill Country Private Estate
          </span>
        </div>
        <span className="font-label-caps text-label-caps text-tertiary uppercase tracking-widest font-semibold">
          Bridal Commission
        </span>
      </div>
    </div>
    <div className="md:col-span-5 flex flex-col gap-gutter">
      <div data-look="editorial" className="flex flex-col bg-surface-container-lowest rounded-xl overflow-hidden shadow-sm group">
        <div className="relative overflow-hidden w-full h-[320px]">
          <img alt="Bridal morning makeup close-up" className="w-full h-full object-cover object-center group-hover:scale-[1.02] transition-transform duration-500 ease-out" src="/images/look-03.jpg" />
          <div className="absolute inset-0 bg-gradient-to-t from-on-background/70 via-transparent to-transparent">
          </div>
          <div className="absolute bottom-0 left-0 p-6 flex flex-col text-surface">
            <span className="px-2.5 py-0.5 bg-primary-container text-on-primary-container rounded font-label-caps text-label-caps uppercase tracking-wider self-start mb-1 font-semibold">
               HD Morning Prep 
            </span>
            <h4 className="font-title-lg text-title-lg text-surface-bright font-semibold">
               Luminous Glass Complexion 
            </h4>
            <p className="font-body-sm text-body-sm text-surface-variant mt-0.5">
               Dewy, weightless skin finish with soft romantic cheek contour. 
            </p>
          </div>
        </div>
        <div className="p-4 px-6 flex items-center justify-between bg-surface-container-lowest">
          <span className="font-body-sm text-body-sm text-on-surface-variant">
            The Driskill Suite • Austin
          </span>
          <span className="material-symbols-outlined text-primary text-[18px]">
            photo_camera
          </span>
        </div>
      </div>
      <div data-look="all" className="bg-surface-container p-6 rounded-xl flex flex-col justify-between flex-grow shadow-sm">
        <div className="flex flex-col">
          <div className="flex items-center gap-1 text-primary mb-3">
            <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>
              star
            </span>
            <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>
              star
            </span>
            <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>
              star
            </span>
            <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>
              star
            </span>
            <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>
              star
            </span>
          </div>
          <p className="font-headline-sm text-headline-sm italic text-on-surface leading-snug font-medium">
             “Isha was calm, completely professional, and pinned my heavy velvet dupatta so securely that I danced all night without a single shift.” 
          </p>
        </div>
        <div className="flex items-center justify-between mt-5 pt-4">
          <div className="flex flex-col">
            <span className="font-title-md text-title-md text-on-surface font-semibold">
              Pooja &amp; Aman
            </span>
            <span className="font-body-sm text-body-sm text-on-surface-variant">
              Lakeway Resort Wedding • Austin, TX
            </span>
          </div>
          <span className="material-symbols-outlined text-secondary text-[24px]">
            favorite
          </span>
        </div>
      </div>
    </div>
    <div data-look="south_asian editorial" className="md:col-span-12 flex flex-col bg-surface-container-lowest rounded-xl overflow-hidden shadow-sm group mt-4">
      <div className="relative overflow-hidden w-full h-[380px] lg:h-[440px]">
        <img alt="Editorial luxury bridal portrait of an Indian bride with flawless glowing makeup artistry" className="w-full h-full object-cover object-center group-hover:scale-[1.01] transition-transform duration-500 ease-out" src="/images/look-02.jpg" />
        <div className="absolute inset-0 bg-gradient-to-t from-on-background/90 via-on-background/30 to-transparent">
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12 flex flex-col md:flex-row md:items-end justify-between gap-6 text-surface">
          <div className="flex flex-col max-w-2xl">
            <span className="px-3 py-1 bg-surface-tint text-on-primary rounded font-label-caps text-label-caps uppercase tracking-wider self-start mb-3 font-semibold">
               Sunset Golden Hour • Austin Texas 
            </span>
            <h3 className="font-headline-lg text-headline-lg-mobile md:text-headline-lg text-surface-bright leading-tight">
               Timeless Warmth &amp; High-Fashion Nuance 
            </h3>
            <p className="font-body-md text-body-md text-surface-variant mt-2 leading-relaxed">
               Soft smoky eye calibration paired with rose-nude velvety lips. Formulated to catch the Texas sunset without flashback or midday oxidation. 
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link className="px-6 py-3 rounded bg-surface-bright text-on-surface hover:bg-surface hover:shadow-lg transition-all duration-200 font-title-md text-title-md flex items-center gap-2 font-semibold" href="/services#portfolio">
              <span>
                View Complete Lookbook
              </span>
              <span className="material-symbols-outlined text-[18px]">
                photo_library
              </span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
  );
}
